/**
 * Filtros de Inventario (INV-01): de la URL a un objeto y de vuelta, y los
 * chips de los que estan puestos. Mismo modelo que `filtros-productos.ts` y
 * `filtros-pedidos.ts`: la URL es la unica fuente.
 *
 * La pantalla tiene dos pestanas y cada una tiene sus filtros. `vista` elige la
 * pestana; cambiar de pestana arranca la otra sin filtros.
 *
 * La pestana Stock filtra con los mismos nombres que la tabla de productos
 * (`stockMin`, `stockMax`, `linea`, `categoria`, `orden`), porque pide la misma
 * API. "Agotados", "Por agotarse" y "Con stock" son rangos con nombre; el tope
 * de "por agotarse" viene del servidor (`ResumenInventario.umbralPorAgotarse`).
 */
import type { OrdenProductosAdmin, TipoMovimientoStock } from '@bandu/compartido';

import { cantidad, compactar, lista, texto, unoDe } from './parametros-url';
import type { FiltroActivo } from './parametros-url';

export type VistaInventario = 'stock' | 'movimientos';

export const PARAMETRO_VISTA = 'vista';

export interface FiltrosStock {
  termino?: string;
  lineas?: string[];
  categorias?: string[];
  stockMin?: number;
  stockMax?: number;
  orden?: OrdenProductosAdmin;
}

export interface FiltrosLibreta {
  termino?: string;
  tipo?: TipoMovimientoStock;
  desde?: string;
  hasta?: string;
}

/** Inventario se abre con lo urgente arriba: lo que menos stock tiene. */
export const ORDEN_STOCK_POR_DEFECTO: OrdenProductosAdmin = 'stock_asc';

export const ORDENES_STOCK: { valor: OrdenProductosAdmin; texto: string }[] = [
  { valor: 'stock_asc', texto: 'Menos stock primero' },
  { valor: 'stock_desc', texto: 'Mas stock primero' },
  { valor: 'nombre', texto: 'Nombre (A a Z)' },
  { valor: 'precio_desc', texto: 'Mayor precio' },
  { valor: 'recientes', texto: 'Recien creados' },
];

export const TIPOS_MOVIMIENTO: { valor: TipoMovimientoStock; texto: string }[] = [
  { valor: 'ingreso', texto: 'Llego mercaderia' },
  { valor: 'venta_web', texto: 'Venta del sitio' },
  { valor: 'venta_externa', texto: 'Venta por fuera' },
  { valor: 'ajuste', texto: 'Conteo o ajuste' },
];

export type EstadoStock = 'agotados' | 'por_agotarse' | 'con_stock';

/** El rango de cada estado con nombre, con el tope que mando el servidor. */
export function rangoDeEstado(
  estado: EstadoStock,
  umbral: number,
): Pick<FiltrosStock, 'stockMin' | 'stockMax'> {
  switch (estado) {
    case 'agotados':
      return { stockMin: undefined, stockMax: 0 };
    case 'por_agotarse':
      return { stockMin: 1, stockMax: umbral };
    case 'con_stock':
      return { stockMin: 1, stockMax: undefined };
  }
}

/** Que estado con nombre es el rango puesto; `rango` si no es ninguno. */
export function estadoDe(filtros: FiltrosStock, umbral: number): EstadoStock | 'rango' | null {
  const { stockMin: minimo, stockMax: maximo } = filtros;
  if (minimo === undefined && maximo === undefined) return null;
  if ((minimo === undefined || minimo === 0) && maximo === 0) return 'agotados';
  if (minimo === 1 && maximo === umbral) return 'por_agotarse';
  if (minimo === 1 && maximo === undefined) return 'con_stock';
  return 'rango';
}

function dia(valor: string | null): string | undefined {
  return valor !== null && /^\d{4}-\d{2}-\d{2}$/.test(valor) ? valor : undefined;
}

export function leerVista(parametros: URLSearchParams): VistaInventario {
  return parametros.get(PARAMETRO_VISTA) === 'movimientos' ? 'movimientos' : 'stock';
}

export function leerFiltrosStock(parametros: URLSearchParams): FiltrosStock {
  const orden = unoDe(
    parametros.get('orden'),
    ORDENES_STOCK.map((opcion) => opcion.valor),
  );
  return compactar({
    termino: texto(parametros.get('termino')),
    lineas: lista(parametros, 'linea'),
    categorias: lista(parametros, 'categoria'),
    stockMin: cantidad(parametros.get('stockMin')),
    stockMax: cantidad(parametros.get('stockMax')),
    // El orden por defecto no se escribe: la URL sin filtros queda limpia.
    orden: orden !== ORDEN_STOCK_POR_DEFECTO ? orden : undefined,
  });
}

export function leerFiltrosLibreta(parametros: URLSearchParams): FiltrosLibreta {
  return compactar({
    termino: texto(parametros.get('termino')),
    tipo: unoDe(
      parametros.get('tipo'),
      TIPOS_MOVIMIENTO.map((opcion) => opcion.valor),
    ),
    desde: dia(parametros.get('desde')),
    hasta: dia(parametros.get('hasta')),
  });
}

/** Las listas van como parametro repetido, igual que en productos. */
export function escribirFiltros(
  vista: VistaInventario,
  filtros: FiltrosStock | FiltrosLibreta,
): URLSearchParams {
  const parametros = new URLSearchParams();
  if (vista === 'movimientos') parametros.set(PARAMETRO_VISTA, vista);

  const nombres: Record<string, string> = { lineas: 'linea', categorias: 'categoria' };
  for (const [clave, valor] of Object.entries(compactar(filtros))) {
    if (clave === 'orden' && valor === ORDEN_STOCK_POR_DEFECTO) continue;
    if (Array.isArray(valor)) {
      for (const elemento of valor) parametros.append(nombres[clave] ?? clave, String(elemento));
    } else {
      parametros.set(clave, String(valor));
    }
  }
  return parametros;
}

export function conCambio<F extends object>(filtros: F, cambio: Partial<F>): F {
  const siguiente: F = { ...filtros, ...cambio };
  // Una lista vacia es lo mismo que no filtrar por ella.
  for (const [clave, valor] of Object.entries(siguiente)) {
    if (Array.isArray(valor) && valor.length === 0) {
      (siguiente as Record<string, unknown>)[clave] = undefined;
    }
  }
  return compactar(siguiente);
}

/** Limpiar todo quita los filtros pero respeta el orden: ordenar no es filtrar. */
export function sinFiltrosStock(filtros: FiltrosStock): FiltrosStock {
  return compactar({ orden: filtros.orden });
}

const TEXTO_ESTADO: Record<EstadoStock, string> = {
  agotados: 'Agotados',
  por_agotarse: 'Por agotarse',
  con_stock: 'Con stock',
};

/** `2026-09-22` -> `22-09-2026`, como se escriben las fechas en Chile. */
function diaChileno(valor: string): string {
  return valor.split('-').reverse().join('-');
}

function textoRango(minimo: string | undefined, maximo: string | undefined, nombre: string): string {
  if (minimo !== undefined && maximo !== undefined) {
    return minimo === maximo ? `${nombre}: ${minimo}` : `${nombre} entre ${minimo} y ${maximo}`;
  }
  if (minimo !== undefined) return `${nombre} desde ${minimo}`;
  return `${nombre} hasta ${maximo ?? ''}`;
}

/**
 * Un chip por filtro de la pestana Stock. Linea y categoria no llevan un chip
 * por valor como en productos: aca se eligen pocas, y los nombres los tiene el
 * selector, que muestra cuantas van.
 */
export function chipsStock(filtros: FiltrosStock, umbral: number): FiltroActivo<FiltrosStock>[] {
  const chips: FiltroActivo<FiltrosStock>[] = [];
  const agregar = (clave: string, texto: string, cambio: Partial<FiltrosStock>) => {
    chips.push({ clave, texto, sinEste: conCambio(filtros, cambio) });
  };

  if (filtros.termino !== undefined) {
    agregar('termino', `Busqueda: "${filtros.termino}"`, { termino: undefined });
  }
  const estado = estadoDe(filtros, umbral);
  if (estado !== null) {
    agregar(
      'stock',
      estado === 'rango'
        ? textoRango(filtros.stockMin?.toString(), filtros.stockMax?.toString(), 'Stock')
        : TEXTO_ESTADO[estado],
      { stockMin: undefined, stockMax: undefined },
    );
  }
  const clasificadas = (filtros.lineas?.length ?? 0) + (filtros.categorias?.length ?? 0);
  if (clasificadas > 0) {
    agregar(
      'categorias',
      `${clasificadas} ${clasificadas === 1 ? 'categoria o linea' : 'categorias o lineas'}`,
      { lineas: undefined, categorias: undefined },
    );
  }
  return chips;
}

export function chipsLibreta(filtros: FiltrosLibreta): FiltroActivo<FiltrosLibreta>[] {
  const chips: FiltroActivo<FiltrosLibreta>[] = [];
  const agregar = (clave: string, texto: string, cambio: Partial<FiltrosLibreta>) => {
    chips.push({ clave, texto, sinEste: conCambio(filtros, cambio) });
  };

  if (filtros.termino !== undefined) {
    agregar('termino', `Producto: "${filtros.termino}"`, { termino: undefined });
  }
  if (filtros.tipo !== undefined) {
    const tipo = TIPOS_MOVIMIENTO.find((opcion) => opcion.valor === filtros.tipo);
    agregar('tipo', tipo?.texto ?? filtros.tipo, { tipo: undefined });
  }
  if (filtros.desde !== undefined || filtros.hasta !== undefined) {
    agregar(
      'fechas',
      textoRango(
        filtros.desde !== undefined ? diaChileno(filtros.desde) : undefined,
        filtros.hasta !== undefined ? diaChileno(filtros.hasta) : undefined,
        'Fecha',
      ),
      { desde: undefined, hasta: undefined },
    );
  }
  return chips;
}
